import {
  Box,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { ReactNode, useState, forwardRef } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

export interface MenuAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  divider?: boolean;
}

interface StepContentBoxProps {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  menuActions?: MenuAction[];
  contentPadding?: number;
  expandAction?: {
    icon: ReactNode;
    onClick: () => void;
  };
}

const StepContentBox = forwardRef<HTMLDivElement, StepContentBoxProps>(
  (
    {
      title,
      children,
      actions,
      footer,
      menuActions,
      contentPadding = 3,
      expandAction,
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (action: MenuAction) => {
      action.onClick();
      handleMenuClose();
    };

    return (
      <Paper
        ref={ref}
        sx={{
          p: 0,
          mt: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {title && (
          <Box
            sx={{
              backgroundColor: "#F6F6F6",
              px: 3,
              py: 0.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                letterSpacing: 1,
                display: "block",
              }}
            >
              {title}
            </Typography>
            {expandAction ? (
              <IconButton
                size="small"
                onClick={expandAction.onClick}
                sx={{ color: "grey.400" }}
              >
                {expandAction.icon}
              </IconButton>
            ) : (
              menuActions &&
              menuActions.length > 0 && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{ color: "grey.400" }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {menuActions.map((action) => (
                      <MenuItem
                        key={action.label}
                        onClick={() => handleMenuItemClick(action)}
                        divider={action.divider}
                      >
                        {action.icon && (
                          <Box sx={{ mr: 1, display: "flex" }}>
                            {action.icon}
                          </Box>
                        )}
                        {action.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )
            )}
          </Box>
        )}

        <Box
          sx={{
            p: contentPadding,
            mb: actions || footer ? 2 : 0,
            flex: 1,
            overflow: "auto",
          }}
        >
          {children}
        </Box>

        {actions && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {actions}
          </Box>
        )}

        {footer && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {footer}
          </Box>
        )}
      </Paper>
    );
  },
);

StepContentBox.displayName = "StepContentBox";

export default StepContentBox;
